import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { HiOutlineDuplicate, HiOutlineEye, HiOutlineLink, HiOutlinePencil, HiOutlineTag } from 'react-icons/hi';
import SideDrawer from '../../../organisms/SideDrawer';
import PageForm from '../../../templates/PageForm';
import { SYSTEM_CONSTANTS } from '../../../../utils/communicationConstants';
import { EmailEnvelope, TemplateVariable } from '../../../../types/communication';
import StatusReasonModal from '../StatusReasonModal';
import SectionCard from '../composition/shared/SectionCard';
import VariableTextField from '../composition/shared/VariableTextField';
import VariablesPanel from '../composition/shared/VariablesPanel';
import { ChannelPill } from '../composition/shared/channelVisuals';
import EmailContentEditor from '../composition/studio/content/email/EmailContentEditor';
import SlackContentEditor from '../composition/studio/content/slack/SlackContentEditor';
import WhatsAppContentEditor from '../composition/studio/content/whatsapp/WhatsAppContentEditor';
import EmailEnvelopeEditor from '../composition/studio/delivery/EmailEnvelopeEditor';
import StudioSkeleton from '../composition/studio/StudioSkeleton';
import TemplateKindPicker from './TemplateKindPicker';
import TemplatePreview from './TemplatePreview';
import UseInCompositionDialog from './UseInCompositionDialog';
import { TEMPLATE_KINDS, templateKind } from './templateKinds';
import { TemplateEditorMode, useTemplateEditor } from './useTemplateEditor';

const LIST_URL = '/communication/templates/0/20/name/ASC';

const secondary =
    'shrink-0 whitespace-nowrap px-4 py-2 rounded-md text-sm bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-slate-700 dark:text-gray-100 dark:hover:bg-slate-600';
const primary =
    'inline-flex items-center gap-1.5 shrink-0 px-4 py-2 rounded-md text-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed';

const TemplateStudioPage: React.FC<{ mode: TemplateEditorMode }> = ({ mode }) => {
    const navigate = useNavigate();
    const { id } = useParams();
    const editor = useTemplateEditor(id, mode);
    const { template, composition, linked, readOnly } = editor;
    const [previewOpen, setPreviewOpen] = useState(false);
    const [toggling, setToggling] = useState(false);
    const [choosingComposition, setChoosingComposition] = useState(false);

    const compositionEditUrl = template && composition ? `/communication/message-composition/${composition.id}/edit?template=${template.id}` : '';

    const save = async () => {
        const saved = await editor.save();
        if (saved) navigate(`/communication/templates/item/${saved.id}`);
    };

    const duplicate = async () => {
        const copy = await editor.duplicate();
        if (copy) navigate(`/communication/templates/item/${copy.id}/edit`);
    };

    const toolbar = () => {
        if (!template) {
            return (
                <div className="flex justify-end m-2">
                    <button type="button" className={secondary} onClick={() => navigate(LIST_URL)}>
                        Voltar
                    </button>
                </div>
            );
        }
        if (mode !== 'view' && !linked) {
            return (
                <div className="flex flex-nowrap items-center justify-end gap-2 m-2">
                    <button type="button" onClick={() => setPreviewOpen(true)} className={`${secondary} xl:hidden inline-flex items-center gap-1.5`}>
                        <HiOutlineEye size={16} /> Pré-visualizar
                    </button>
                    <button
                        type="button"
                        className={secondary}
                        disabled={editor.saving}
                        onClick={() => navigate(mode === 'edit' ? `/communication/templates/item/${template.id}` : LIST_URL)}
                    >
                        {mode === 'edit' && editor.dirty ? 'Descartar' : 'Cancelar'}
                    </button>
                    <button type="button" className={primary} onClick={save} disabled={editor.saving || (mode === 'edit' && !editor.dirty)}>
                        {mode === 'new' ? 'Criar modelo' : 'Salvar'}
                    </button>
                </div>
            );
        }
        const active = template.status === 'ACTIVE';
        return (
            <div className="flex flex-nowrap items-center justify-end gap-2 m-2">
                <button type="button" onClick={() => setPreviewOpen(true)} className={`${secondary} xl:hidden inline-flex items-center gap-1.5`}>
                    <HiOutlineEye size={16} /> Pré-visualizar
                </button>
                <button type="button" onClick={duplicate} className={`${secondary} inline-flex items-center gap-1.5`}>
                    <HiOutlineDuplicate size={16} /> Duplicar
                </button>
                {!linked && (
                    <button
                        type="button"
                        onClick={() => setChoosingComposition(true)}
                        disabled={!editor.compatibleCompositions.length}
                        title={editor.compatibleCompositions.length ? undefined : 'Nenhuma composição compatível'}
                        className={`${secondary} inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                        <HiOutlineLink size={16} /> Usar em composição
                    </button>
                )}
                {linked ? (
                    <button type="button" className={primary} onClick={() => navigate(compositionEditUrl)}>
                        <HiOutlinePencil size={16} /> Editar
                    </button>
                ) : (
                    <button type="button" className={primary} onClick={() => navigate(`/communication/templates/item/${template.id}/edit`)}>
                        <HiOutlinePencil size={16} /> Editar
                    </button>
                )}
                <button
                    type="button"
                    onClick={() => setToggling(true)}
                    className={`shrink-0 whitespace-nowrap px-4 py-2 rounded-md text-sm text-white ${
                        active ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
                    }`}
                >
                    {active ? 'Desativar' : 'Ativar'}
                </button>
            </div>
        );
    };

    const contentEditor = () => {
        if (!template) return null;
        const kind = templateKind(template);
        if (kind === 'EMAIL_ENVELOPE' && template.envelope) {
            return (
                <EmailEnvelopeEditor
                    envelope={template.envelope}
                    variables={template.variables}
                    disabled={readOnly}
                    onChange={(envelope: EmailEnvelope) => editor.update({ envelope })}
                />
            );
        }
        if (kind === 'EMAIL_BODY') {
            return <EmailContentEditor key={template.id} template={template} disabled={readOnly} onTemplateChange={editor.update} />;
        }
        if (kind === 'INTERNAL_MESSAGE') {
            return <SlackContentEditor key={template.id} template={template} disabled={readOnly} onChange={(content) => editor.update({ content })} />;
        }
        return <WhatsAppContentEditor key={template.id} template={template} disabled={readOnly} catalog={editor.providerCatalog} onChange={editor.update} />;
    };

    const body = () => {
        if (!template) return null;
        const kindInfo = TEMPLATE_KINDS[templateKind(template)];
        const constants = SYSTEM_CONSTANTS.filter((c) => editor.constantNames.includes(c.name));
        const updateVariable = (name: string, patch: Partial<TemplateVariable>) =>
            editor.update({ variables: template.variables.map((v) => (v.name === name ? { ...v, ...patch } : v)) });
        const removeVariable = (name: string) => editor.update({ variables: template.variables.filter((v) => v.name !== name) });

        return (
            <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_400px] gap-5 py-4 xl:h-full">
                <main className="min-w-0 xl:overflow-y-auto pb-10 pr-1 flex flex-col gap-4">
                    {linked && composition && (
                        <div className="flex flex-wrap items-center gap-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-900 px-4 py-3 text-sm text-amber-900 dark:text-amber-200">
                            <HiOutlineLink size={18} className="shrink-0" />
                            <span className="flex-1 min-w-0">
                                Este modelo faz parte da composição <strong>{composition.description || composition.name}</strong>. Para mudar,
                                edite pela composição.
                            </span>
                            <button type="button" className="text-sm font-medium underline" onClick={() => navigate(compositionEditUrl)}>
                                Editar
                            </button>
                        </div>
                    )}
                    <SectionCard title="Sobre o modelo" description="Como o modelo aparece nas listas." icon={<HiOutlineTag size={18} />}>
                        <div className="grid grid-cols-1 gap-4">
                            <VariableTextField
                                id="template-name"
                                label="Nome do modelo"
                                value={template.name}
                                disabled={readOnly}
                                maxLength={120}
                                error={!readOnly && !template.name.trim() ? 'Obrigatório' : undefined}
                                onChange={(name) => editor.update({ name })}
                            />
                            <VariableTextField
                                id="template-description"
                                label="Para que serve"
                                value={template.description ?? ''}
                                disabled={readOnly}
                                multiline
                                rows={2}
                                maxLength={240}
                                onChange={(description) => editor.update({ description })}
                            />
                            <div className="flex flex-wrap items-center gap-3">
                                <ChannelPill channel={template.channel} />
                                <span className="text-xs text-gray-500">
                                    {kindInfo.label} · {kindInfo.format} · {kindInfo.storage}
                                </span>
                            </div>
                        </div>
                    </SectionCard>
                    {contentEditor()}
                    <VariablesPanel
                        variables={template.variables}
                        unused={editor.unused}
                        constants={constants}
                        disabled={readOnly}
                        onUpdate={updateVariable}
                        onRemove={removeVariable}
                    />
                </main>
                <aside className="hidden xl:block overflow-y-auto pb-10 pr-1">
                    <TemplatePreview template={template} catalog={editor.providerCatalog} />
                </aside>
            </div>
        );
    };

    const serialLabel = template?.id ? `Modelo #${template.id}` : 'Modelo';
    const title = mode === 'new' ? 'Novo modelo' : mode === 'edit' ? 'Edição' : serialLabel;

    const breadcrumb =
        mode === 'new'
            ? [
                  { label: 'Modelos', url: '#', onClick: () => navigate(LIST_URL) },
                  { label: 'Novo', url: '#' },
              ]
            : mode === 'edit'
              ? [
                    { label: 'Modelos', url: '#', onClick: () => navigate(LIST_URL) },
                    {
                        label: serialLabel,
                        url: '#',
                        onClick: template ? () => navigate(`/communication/templates/item/${template.id}`) : undefined,
                    },
                    { label: 'Edição', url: '#' },
                ]
              : [
                    { label: 'Modelos', url: '#', onClick: () => navigate(LIST_URL) },
                    { label: serialLabel, url: '#' },
                ];

    return (
        <PageForm
            pageTitle={title}
            truncateTitle
            apiVersion={editor.apiVersion}
            isLoading={editor.loading || editor.saving}
            breadcrumb={breadcrumb}
            controlSlot={toolbar()}
        >
            {editor.loading && mode !== 'new' && (
                <div className="py-2">
                    <StudioSkeleton mode={mode} />
                </div>
            )}
            {!template && mode === 'new' && <TemplateKindPicker onPick={editor.start} />}
            {!editor.loading && body()}
            <SideDrawer open={previewOpen} title="Pré-visualização" width="440px" onClose={() => setPreviewOpen(false)}>
                {previewOpen && template && <TemplatePreview template={template} catalog={editor.providerCatalog} />}
            </SideDrawer>
            <StatusReasonModal
                isOpen={toggling}
                title={template?.status === 'ACTIVE' ? 'Desativar modelo' : 'Ativar modelo'}
                actionLabel={template?.status === 'ACTIVE' ? 'Desativar' : 'Ativar'}
                onCancel={() => setToggling(false)}
                onConfirm={async (reason) => {
                    setToggling(false);
                    await editor.toggleStatus(reason);
                }}
            />
            <UseInCompositionDialog
                show={choosingComposition}
                compositions={editor.compatibleCompositions}
                onClose={() => setChoosingComposition(false)}
                onPick={async (target) => {
                    if (template && (await editor.linkToComposition(target))) {
                        navigate(`/communication/message-composition/${target.id}/edit?template=${template.id}`);
                    }
                }}
            />
        </PageForm>
    );
};

export default TemplateStudioPage;
